import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { MessageSquare, Send, Search, RefreshCw, Wifi, WifiOff, Loader2, ArrowLeft } from 'lucide-react';
import * as whatsappService from '../services/whatsappService.ts';

interface Chat {
  id: string;
  name: string;
  lastMessage: string;
  timestamp: string | null;
  unreadCount: number;
}

interface WaMessage {
  id: number;
  chat_id: string;
  chat_name: string | null;
  phone_number: string | null;
  message: string | null;
  from_me: boolean;
  message_timestamp: string;
}

const formatTime = (ts: string | null) => {
  if (!ts) return '';
  return new Date(ts).toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' });
};

const formatDate = (ts: string | null) => {
  if (!ts) return '';
  const d = new Date(ts);
  const today = new Date();
  if (d.toDateString() === today.toDateString()) return 'Hoy';
  return d.toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit' });
};

const WhatsAppAdmin: React.FC = () => {
  const [status, setStatus] = useState<string>('disconnected');
  const [qr, setQr] = useState<string | null>(null);
  const [chats, setChats] = useState<Chat[]>([]);
  const [selectedChat, setSelectedChat] = useState<Chat | null>(null);
  const [messages, setMessages] = useState<WaMessage[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const qrPollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const checkStatus = useCallback(async () => {
    try {
      const data = await whatsappService.getStatus();
      setStatus(data.status);
      if (data.status === 'disconnected') {
        setQr(null);
      }
      if (data.status === 'qr_pending') {
        try {
          const qrData = await whatsappService.getQR();
          if (qrData.qr) {
            setQr(qrData.qr);
          }
        } catch (_) {}
      }
    } catch (err: any) {
      console.error('Error checking WhatsApp status:', err);
    }
  }, []);

  const loadQR = async () => {
    if (qrPollRef.current) clearInterval(qrPollRef.current);
    try {
      setLoading(true);
      await whatsappService.connect();
      let attempts = 0;
      qrPollRef.current = setInterval(async () => {
        attempts++;
        try {
          const data = await whatsappService.getQR();
          if (data.qr) {
            setQr(data.qr);
            setStatus(data.status);
            if (qrPollRef.current) clearInterval(qrPollRef.current);
            setLoading(false);
          } else if (data.status === 'connected') {
            if (qrPollRef.current) clearInterval(qrPollRef.current);
            setStatus('connected');
            setLoading(false);
          } else if (attempts >= 30) {
            if (qrPollRef.current) clearInterval(qrPollRef.current);
            setLoading(false);
          }
        } catch (err: any) {
          console.error('QR poll error:', err);
        }
      }, 1000);
    } catch (err: any) {
      console.error('Error connecting WhatsApp:', err);
      setLoading(false);
    }
  };

  const loadChats = useCallback(async (retries = 3, delay = 2000) => {
    for (let i = 0; i < retries; i++) {
      try {
        const data = await whatsappService.getChats();
        if (data && data.length > 0) {
          setChats(data);
          return;
        }
        if (i < retries - 1) {
          await new Promise((r) => setTimeout(r, delay));
        }
      } catch (err: any) {
        console.error('Error loading chats:', err);
        if (i < retries - 1) {
          await new Promise((r) => setTimeout(r, delay));
        }
      }
    }
  }, []);

  const loadMessages = useCallback(async (chatId: string) => {
    try {
      const data = await whatsappService.getMessages(chatId);
      setMessages(
        [...(data || [])].sort(
          (a, b) => new Date(a.message_timestamp).getTime() - new Date(b.message_timestamp).getTime(),
        ),
      );
      setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    } catch (err: any) {
      console.error('Error loading messages:', err);
    }
  }, []);

  const handleSend = async () => {
    if (!newMessage.trim() || !selectedChat) return;
    setSending(true);
    try {
      await whatsappService.sendMessage(selectedChat.id, newMessage.trim());
      setNewMessage('');
      await loadMessages(selectedChat.id);
    } catch (err: any) {
      console.error('Error sending message:', err);
    } finally {
      setSending(false);
    }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      loadChats();
      return;
    }
    try {
      const data = await whatsappService.searchChats(searchQuery);
      setChats(data);
    } catch (err) {
      console.error('Error searching:', err);
    }
  };

  const handleDisconnect = async () => {
    if (qrPollRef.current) clearInterval(qrPollRef.current);
    try {
      await whatsappService.disconnect();
      setStatus('disconnected');
      setQr(null);
      setChats([]);
      setSelectedChat(null);
      setMessages([]);
    } catch (err: any) {
      console.error('Error disconnecting WhatsApp:', err);
    }
  };

  useEffect(() => {
    checkStatus();
    const interval = setInterval(checkStatus, 10000);
    return () => clearInterval(interval);
  }, [checkStatus]);

  useEffect(() => {
    if (status === 'connected') {
      const timer = setTimeout(() => {
        loadChats();
      }, 8000);
      const interval = setInterval(() => loadChats(1, 0), 30000);
      return () => {
        clearTimeout(timer);
        clearInterval(interval);
      };
    }
  }, [status, loadChats]);

  useEffect(() => {
    if (selectedChat) {
      loadMessages(selectedChat.id);
      const interval = setInterval(() => loadMessages(selectedChat.id), 15000);
      return () => clearInterval(interval);
    }
  }, [selectedChat?.id, loadMessages]);

  useEffect(() => {
    return () => {
      if (qrPollRef.current) clearInterval(qrPollRef.current);
    };
  }, []);

  const filteredChats = useMemo(() =>
    chats.filter((c) =>
      c.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      c.id.includes(searchQuery),
    ), [chats, searchQuery]);

  return (
    <div className="flex h-[calc(100vh-64px)] bg-gray-100">
      {/* Sidebar - Chat list */}
      <div className="w-80 bg-white border-r border-gray-200 flex flex-col">
        <div className="p-4 border-b border-gray-200">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-green-600" />
              WhatsApp
            </h2>
            <div className="flex items-center gap-1">
              {status === 'connected' ? (
                <span className="flex items-center gap-1 text-xs text-green-600 bg-green-50 px-2 py-1 rounded-full">
                  <Wifi className="w-3 h-3" /> Conectado
                </span>
              ) : status === 'qr_pending' ? (
                <span className="flex items-center gap-1 text-xs text-yellow-600 bg-yellow-50 px-2 py-1 rounded-full">
                  <Loader2 className="w-3 h-3 animate-spin" /> Esperando QR
                </span>
              ) : (
                <span className="flex items-center gap-1 text-xs text-red-600 bg-red-50 px-2 py-1 rounded-full">
                  <WifiOff className="w-3 h-3" /> Desconectado
                </span>
              )}
            </div>
          </div>

          {status === 'disconnected' && (
            <button
              onClick={loadQR}
              disabled={loading}
              className="w-full bg-green-600 hover:bg-green-700 text-white py-2 px-4 rounded-lg flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wifi className="w-4 h-4" />}
              {loading ? 'Conectando...' : 'Conectar WhatsApp'}
            </button>
          )}

          {status === 'connected' && (
            <div className="flex gap-2">
              <div className="flex-1 relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Buscar chats..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                  className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                />
              </div>
              <button
                onClick={handleDisconnect}
                className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                title="Desconectar"
              >
                <WifiOff className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>

        {/* QR Display */}
        {status === 'qr_pending' && (
          <div className="p-4 flex flex-col items-center border-b border-gray-200">
            {qr ? (
              <>
                <p className="text-sm text-gray-600 mb-3">Escaneá el QR con tu WhatsApp</p>
                <img src={qr} alt="QR Code" className="w-56 h-56 rounded-lg border" />
                <button
                  onClick={loadQR}
                  className="mt-3 text-sm text-green-600 hover:text-green-700 flex items-center gap-1"
                >
                  <RefreshCw className="w-3 h-3" /> Regenerar QR
                </button>
              </>
            ) : (
              <div className="flex items-center gap-2 text-gray-500">
                <Loader2 className="w-4 h-4 animate-spin" /> Cargando QR...
              </div>
            )}
          </div>
        )}

        {/* Chat list */}
        {status === 'connected' && (
          <div className="flex-1 overflow-y-auto">
            {filteredChats.length === 0 ? (
              <div className="p-4 text-center text-gray-500 text-sm">
                No hay chats
              </div>
            ) : (
              filteredChats.map((chat) => (
                <div
                  key={chat.id}
                  onClick={() => setSelectedChat(chat)}
                  className={`p-3 border-b border-gray-100 cursor-pointer hover:bg-gray-50 transition ${
                    selectedChat?.id === chat.id ? 'bg-green-50 border-l-4 border-l-green-600' : ''
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-800 truncate text-sm">{chat.name}</p>
                      <p className="text-xs text-gray-500 truncate">{chat.lastMessage}</p>
                    </div>
                    <div className="flex flex-col items-end ml-2">
                      <span className="text-xs text-gray-400">{formatDate(chat.timestamp)}</span>
                      <span className="text-xs text-gray-400">{formatTime(chat.timestamp)}</span>
                      {chat.unreadCount > 0 && (
                        <span className="mt-1 bg-green-600 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                          {chat.unreadCount}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

      </div>

      {/* Main - Messages */}
      <div className="flex-1 flex flex-col">
        {selectedChat ? (
          <>
            {/* Header */}
            <div className="bg-green-600 text-white p-3 flex items-center gap-3">
              <button
                onClick={() => setSelectedChat(null)}
                className="md:hidden p-1 hover:bg-green-700 rounded"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div className="flex-1">
                <p className="font-bold">{selectedChat.name}</p>
                <p className="text-xs text-green-100">{selectedChat.id}</p>
              </div>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 bg-[#e5ddd5] bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiMwMDAiIGZpbGwtb3BhY2l0eT0iMC4wMiI+PHBhdGggZD0iTTM2IDM0djItSDJ2LTJoMzRtMC04djJIMnYtMmgzNG0wLTh2Mkgdi0yaDM0Ii8+PC9nPjwvZz48L3N2Zz4=')]">
              {messages.length === 0 ? (
                <div className="text-center text-gray-500 text-sm mt-10">
                  No hay mensajes en este chat
                </div>
              ) : (
                [...messages].map((msg) => (
                  <div
                    key={msg.id}
                    className={`flex ${msg.from_me ? 'justify-end' : 'justify-start'} mb-2`}
                  >
                    <div
                      className={`max-w-[70%] px-3 py-2 rounded-lg shadow-sm ${
                        msg.from_me
                          ? 'bg-[dcf8c6] text-gray-800 rounded-br-none'
                          : 'bg-white text-gray-800 rounded-bl-none'
                      }`}
                    >
                      <p className="text-sm whitespace-pre-wrap">{msg.message}</p>
                      <p className="text-[10px] text-gray-500 text-right mt-1">
                        {formatTime(msg.message_timestamp)}
                      </p>
                    </div>
                  </div>
                ))
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            <div className="p-3 bg-gray-50 border-t border-gray-200">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSend()}
                  placeholder="Escribí un mensaje..."
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                  disabled={sending}
                />
                <button
                  onClick={handleSend}
                  disabled={!newMessage.trim() || sending}
                  className="bg-green-600 hover:bg-green-700 text-white p-3 rounded-full disabled:opacity-50 transition"
                >
                  {sending ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
                </button>
              </div>
            </div>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center text-gray-400">
            <div className="text-center">
              <MessageSquare className="w-16 h-16 mx-auto mb-4 opacity-30" />
              <p className="text-lg">Seleccioná un chat para ver los mensajes</p>
              <p className="text-sm mt-1">o conectá WhatsApp para empezar</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default WhatsAppAdmin;
